import { FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from '@reduxjs/toolkit/query'
import { AuthServiceError, HcError, ProblemDetails } from '@/common/types/api.def.ts'
import { Button, notification } from 'antd'
import { CopyButton } from '@/common/components/CopyButton.tsx'
import { nrba } from '@/new-relic-init.ts'
import { decodeToken } from '@/features/auth/auth.utils.ts'

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export const isFetchBaseQueryError = (
  error: unknown,
): error is FetchBaseQueryError => {
  return typeof error === 'object' && error != null && 'status' in error
}

export const isHcError = (obj: unknown): obj is HcError => {
  return typeof obj === 'object' && obj != null && 'errorCode' in obj && 'message' in obj && 'details' in obj
}

export const isProblemDetails = (obj: unknown): obj is ProblemDetails => {
  return typeof obj === 'object' && obj != null
    && 'errors' in obj && 'title' in obj && 'extensions' in obj
}

export const isAuthServiceError = (obj: unknown): obj is AuthServiceError => {
  return typeof obj === 'object' && obj != null
    && 'timestamp' in obj && 'message' in obj && 'valid' in obj && 'statusCode' in obj
}

export const isFetchBaseQueryHcError = (
  error: unknown,
): error is { status: number, data: HcError } => {
  return isFetchBaseQueryError(error) && isHcError(error.data)
}

export const handleApiError = (error: unknown, message: string = '') => {
  if (isFetchBaseQueryError(error)) {
    // backend controller validation error
    if (isProblemDetails(error.data)) {
      notification.error({
        message,
        description: JSON.stringify(error.data.errors),
        actions: <CopyButton copyData={error.data}>Copy Error</CopyButton>,
      })
    } else if (isAuthServiceError(error.data)) {
      notification.error({
        message,
        description: error.data.message,
        actions: <CopyButton copyData={error.data}>Copy Error</CopyButton>,
      })
    } else if (isHcError(error.data)) {
      notification.error({
        message,
        description: error.data.message,
        actions: <CopyButton copyData={error.data}>Copy Error</CopyButton>,
      })
    }
  }
}

export const getHcErrorData = <T = unknown, >(error: unknown, message = 'Failed') => {
  if (isFetchBaseQueryError(error)) {
    if (isProblemDetails(error.data)) {
      notification.error({
        message,
        description: error.data.detail,
        btn: <Button onClick={() => navigator.clipboard.writeText(JSON.stringify(error.data, null, 2))}>Copy
          Error</Button>,
      })
      return null
    } else if (isHcError(error.data)) {
      return error.data as HcError<T>
    }
  }
}

export const handleSessionExpiredError = (
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
  args: string | FetchArgs,
) => {
  if ((result.error && result.error.status === 302) || (result.error && result.error.status === 401)) {
    const authorization = result.meta?.request.headers.get('Authorization')
    const token = authorization?.split(' ')[1] ?? ''
    const hasToken = !!token
    const user = hasToken ? decodeToken(token) : null
    const tokenExpTime = user ? new Date(user.exp * 1000).toISOString() : ''

    const track = typeof args === 'string' ? {
      hasToken,
      tokenExpTime,
    } : {
      url: args.url,
      hasToken,
      tokenExpTime,
    }
    nrba.noticeError('session_expired_error', {
      statusCode: result.error.status,
      ...track,
    })
    notification.error({
      key: 'session-expired',
      message: 'Session Expired',
      description: 'Your session has expired. Please login again. Please save your unsaved work before refreshing the page.',
      duration: null,
      actions:
        <Button
          danger onClick={() => {
          window.location.reload()
        }}
        >
          Refresh
        </Button>,
    })
  }
}

export const handleNetworkError = (
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>
) => {
  if (result.error?.status === "FETCH_ERROR") {
    notification.error({
      key: 'fetch-error',
      message: 'Network Error',
      description: 'Failed to connect to the server. Please check your network and retry later.',
      duration: null,
      actions:
        <Button
          danger onClick={() => {
          window.location.reload()
        }}
        >
          Refresh
        </Button>,
    })
  }
}